import {
  APIV1Version,
  ClientType,
  getStoreName,
} from '@buncha/common/types/common';
import {isNSC2, isPlu} from '@buncha/batchDetails/utils/ScannedAddItem';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {ActivityIndicator} from 'react-native';
import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreenParam} from '@buncha/B2B/types';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import BinIcon from '@buncha/components/core/icon/BinIcon';
import {Box} from '@buncha/components/core/view';
import {CustomText} from '@buncha/components/core/text';
import {EventType} from '@buncha/config/analytics/type';
import FailedOrderItem from '../FailedOrderItem';
import {HTouchable} from '@buncha/components/core/touchable';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {Modules} from '@buncha/utils/types';
import {OrderItems} from '@buncha/batchDetails/types';
import ReScanIcon from '@buncha/components/core/icon/ReScanIcon';
import React from 'react';
import {navigation} from '@buncha/config/navigation';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {styles} from './styles';
import {useB2BCheckoutContext} from '@buncha/B2B/hooks/B2BCheckoutReducer';
import {useCallback} from 'react';
import {useDropOrderItem} from '@buncha/itemDetails/hooks/ItemDetails';
import {useGetLocale} from '@buncha/localization/hooks';
import {useGetOrdersForValidationAndUpdationCheckout} from '@buncha/B2B/hooks/B2BChekout';
import {useRoute} from '@react-navigation/native';

interface FailedOrderItemProps {
  item: OrderItems;
  orderId: number;
}

const FailedOrderItemList: React.FC<FailedOrderItemProps> = props => {
  const {item, orderId} = props;
  const {dispatch} = useB2BCheckoutContext();
  const routeParams = useRoute()?.params as B2BScreenParam | undefined;
  const {dropOrderItem, droppingItem} = useDropOrderItem();
  const muliFont = useFontTheme('muli');
  const theme = useColorThemeType();
  const [getOrdersForValidationAndUpdation, checkoutState] =
    useGetOrdersForValidationAndUpdationCheckout(APIV1Version.V100, dispatch);
  const runId = routeParams?.runId ?? 0;
  const storeId = routeParams?.storeId ?? 0;
  const {body} = useGetLocale(Modules.B2B, HomeScreens.B2B) ?? {};
  const storeIndex = routeParams?.storeIndex ?? '';

  const loading = checkoutState.loading || droppingItem;

  const navigateToScanPage = useCallback(() => {
    if (isNSC2(item?.upcOriginal ?? '') || isPlu(item?.upcOriginal ?? ''))
      navigation.navigate(HomeScreens.B2B, {
        screen: ItemDetailsScreen.NSCItemScanner,
        params: {
          orderItemId: item?.id,
          storeIndex,
          runId,
          catalogItemId: item?.catalogItemId,
          returnPage: B2BScreens.B2BCheckout,
        },
      });
    else
      navigation.navigate(HomeScreens.B2B, {
        screen: ItemDetailsScreen.ItemScanner,
        params: {
          orderItemId: item?.id,
          storeIndex,
          runId,
          quantity: item?.quantity,
          orderItemIds: [item?.id],
          source: 'Verification Page',
          returnPage: B2BScreens.B2BCheckout,
        },
      });
  }, [
    item?.catalogItemId,
    item?.id,
    item?.quantity,
    item?.upcOriginal,
    runId,
    storeIndex,
  ]);

  const dropItem = useCallback(
    async (itemId: number) => {
      Analytics.eventWithProps('item drop', {
        type: EventType.cta,
        runId: runId,
        client: ClientType.meijer,
        name: item?.name,
        category: item?.tags,
        legacyId: orderId,
        storeType: getStoreName(String(storeId)),
      });
      await dropOrderItem(runId, [itemId]);
      getOrdersForValidationAndUpdation(runId);
    },
    [
      dropOrderItem,
      getOrdersForValidationAndUpdation,
      item?.name,
      item?.tags,
      orderId,
      runId,
      storeId,
    ],
  );

  return (
    <FailedOrderItem
      itemImage={item.itemImageUrl}
      itemName={item?.name ?? ''}
      subText={`Qty: ${item?.quantity}`}
      leftComponent={
        <React.Fragment>
          <If condition={!loading}>
            <Box style={styles.buttonContainer}>
              <HTouchable
                onPress={navigateToScanPage}
                style={[
                  styles.topButtonContainer,
                  {
                    backgroundColor: theme?.background.blue[100],
                    borderColor: theme?.background.blue[100],
                  },
                ]}
                disabled={droppingItem}>
                <ReScanIcon
                  height={scaleFonts(15)}
                  width={scaleFonts(15)}
                  fill={theme?.background.blue[100]}
                  viewBox="0 0 15 15"
                  style={styles.rightMargin}
                />
                <CustomText
                  style={[
                    styles.leftAlign,
                    {
                      fontFamily: muliFont[700].normal,
                      color: theme?.background.white[100],
                    },
                  ]}>
                  {body?.rescan}
                </CustomText>
              </HTouchable>
              <HTouchable
                onPress={async () => await dropItem(item?.id)}
                style={[
                  styles.bottomButtonContainer,
                  {
                    borderColor: theme?.background.blue[100],
                  },
                ]}
                disabled={droppingItem}>
                <BinIcon
                  height={scaleFonts(15)}
                  width={scaleFonts(15)}
                  viewBox="-10 -5 25 25"
                  fill={theme?.background.white[100]}
                  stroke={theme?.background.blue[100]}
                  strokeWidth={2}
                  style={styles.rightMargin}
                />
                <CustomText
                  style={[
                    styles.leftAlign,
                    {
                      fontFamily: muliFont[700].normal,
                      color: theme?.background.blue[100],
                    },
                  ]}>
                  {body?.drop}
                </CustomText>
              </HTouchable>
            </Box>
          </If>
          <If condition={loading}>
            <ActivityIndicator />
          </If>
        </React.Fragment>
      }
    />
  );
};

export default React.memo(FailedOrderItemList);
